import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Psychology,
  TrendingUp,
  Forum as ForumIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  CancelOutlined as CancelOutlinedIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { AIInsightsData } from '../types';
import { doctorService } from '../services/doctorService';
import { getErrorMessage } from '../utils/errorMessage';

interface AIInsightsProps {
  companyCode: string;
  customerCode: string;
  month: string;
}

const createKeyedItems = (items: string[]) => {
  const seenItems = new Map<string, number>();

  return items.map((value) => {
    const occurrences = (seenItems.get(value) ?? 0) + 1;
    seenItems.set(value, occurrences);

    return {
      key: `${value}-${occurrences}`,
      value,
    };
  });
};

export const AIInsights: React.FC<AIInsightsProps> = ({ companyCode, customerCode, month }) => {
  const [data, setData] = useState<AIInsightsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchInsights = async () => {
      if (!companyCode || !customerCode || !month) {
        setData(null);
        setError('');
        return;
      }

      try {
        setLoading(true);
        setError('');
        const insights = await doctorService.getAIInsights(companyCode, customerCode, month);
        setData(insights);
      } catch (error: unknown) {
        console.error('Failed to load AI insights', error);
        setError(`Unable to load AI insights: ${getErrorMessage(error)}`);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    void fetchInsights();
  }, [companyCode, customerCode, month]);

  if (!customerCode) {
    return <Alert severity="info">Select a doctor to view AI insights.</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No AI insights available.</Alert>;
  }

  return (
    <Box sx={{ pr: { xs: 0, sm: 2 }, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          AI Insights
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Psychology sx={{ color: '#ff9800' }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  Doctor persona
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {data.doctor_persona}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp sx={{ color: '#ff9800' }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  Engagement signals
                </Typography>
              </Box>
              <List dense disablePadding>
                {createKeyedItems(data.engagement_signals).map(({ key: itemKey, value }) => (
                  <ListItem key={itemKey} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
                      <NavigateNextIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} primary={value} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ForumIcon sx={{ color: '#ff9800' }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  Recommended discussion areas
                </Typography>
              </Box>
              <Grid container spacing={1}>
                {createKeyedItems(data.recommended_discussion_areas).map(({ key: itemKey, value }) => (
                  <Grid item key={itemKey}>
                    <Chip label={value} variant="outlined" sx={{ borderColor: '#C16D18', color: '#5d4037' }} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: '#2e7d32' }} fontSize="small" />
                Do&apos;s
              </Typography>
              <List dense disablePadding>
                {createKeyedItems(data.dos).map(({ key: itemKey, value }) => (
                  <ListItem key={itemKey} disableGutters sx={{ py: 0.35 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={value} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CancelOutlinedIcon sx={{ color: '#c62828' }} fontSize="small" />
                Don&apos;ts
              </Typography>
              <List dense disablePadding>
                {createKeyedItems(data.donts).map(({ key: itemKey, value }) => (
                  <ListItem key={itemKey} disableGutters sx={{ py: 0.35 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CancelOutlinedIcon fontSize="small" sx={{ color: '#c62828' }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={value} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: 2,
              bgcolor: '#fff8e1',
              borderLeft: '4px solid #ff9800',
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Next best action
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2">{data.next_best_action}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
